import { Fragment, FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { getItemTitle, NavigationPageItem } from '@cbridge-utils/navigation';
import { getInitials } from '@cbridge-utils/shared-helpers';
import { ToggleExpandButton } from '@cbridge-tools/button';
import { NavigationItemHorizontalPadding, NavigationItemVerticalPadding } from '../NavigationItem.interface';
import { NavigationListItemButton } from '../NavigationListItemButton/NavigationListItemButton';
import { CollapsibleSubNavigation } from '../CollapsibleSubNavigation/CollapsibleSubNavigation';
import { INavigationLinkItemProps } from './NavigationLinkItem.interface';

export const NavigationLinkItem: FunctionComponent<INavigationLinkItemProps> = (props) => {
  const {
    navigationItem,
    fullPath,
    isNestedNavigationExpanded,
    isSelected,
    depth,
    isMini,
    isFullyExpanded,
    toggleExpand,
    SubNavigationComponent,
    variant = 'primary',
  } = props;

  const getVariantStyles = () => {
    switch (variant) {
      case 'secondary':
        return {
          verticalPadding: NavigationItemVerticalPadding.md,
          horizontalPadding: NavigationItemHorizontalPadding.md,
          fontSize: 'var(--cbridge-font-size-sm)',
        };
      default:
        return {
          verticalPadding: NavigationItemVerticalPadding.lg,
          horizontalPadding: NavigationItemHorizontalPadding.lg,
          fontSize: 'var(--cbridge-font-size-md)',
        };
    }
  };

  const { verticalPadding, horizontalPadding, fontSize } = getVariantStyles();
  const {
    children, icon, action, segment, isActionAlwaysVisible, disabled, disabledTooltip,
  } = navigationItem;

  const navigationItemTitle = getItemTitle(navigationItem);

  const listItemIconSize = 34;

  const toggleExpandButton =
    children && !isMini ? (
      <ToggleExpandButton
        isExpanded={isNestedNavigationExpanded}
        onChange={toggleExpand}
        size={'medium'}
      />
    ) : undefined;

  const listItem = (
    <ListItem
      sx={{
        py: 0,
        px: 0,
        overflowX: 'hidden',
      }}
      secondaryAction={toggleExpandButton}
    >
      <NavigationListItemButton
        isActionAlwaysVisible={isActionAlwaysVisible}
        depth={depth}
        py={verticalPadding}
        px={horizontalPadding}
        selected={isSelected}
        disabled={disabled}
        {...(children && !isMini && !segment
          ? { onClick: toggleExpand }
          : { component: Link, to: fullPath ?? segment })}
      >
        {icon || isMini ? (
          <ListItemIcon
            sx={{
              minWidth: 40,
            }}
          >
            {icon ?? null}
            {!icon && isMini ? (
              <Avatar
                sx={{
                  width: listItemIconSize - 7,
                  height: listItemIconSize - 7,
                  fontSize,
                  ml: '-2px',
                }}
              >
                {getInitials(navigationItemTitle)}
              </Avatar>
            ) : null}
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={navigationItemTitle}
          slotProps={{
            primary: {
              noWrap: true,
              sx: { fontSize },
            },
          }}
          sx={{
            my: 0,
            zIndex: 1,
          }}
        />
        {action && !isMini && isFullyExpanded ? action : null}
      </NavigationListItemButton>
    </ListItem>
  );

  const tooltipTitle = disabled && disabledTooltip ? disabledTooltip : isMini ? navigationItemTitle : '';

  return (
    <Fragment>
      {tooltipTitle ? (
        <Tooltip title={tooltipTitle} placement={isMini ? 'right' : undefined}>
          {listItem}
        </Tooltip>
      ) : (
        listItem
      )}

      {navigationItem.children && !isMini ? (
        <CollapsibleSubNavigation
          items={navigationItem.children as NavigationPageItem[]}
          basePath={fullPath}
          depth={depth + 1}
          isNestedNavigationExpanded={isNestedNavigationExpanded}
          SubNavigationComponent={SubNavigationComponent}
        />
      ) : null}
    </Fragment>
  );
};
